import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { motion } from "framer-motion";
import { 
  ChevronRight, 
  ArrowLeft, 
  Shield, 
  Save,
  AlertCircle,
  Check,
  X,
  Users,
  AlertTriangle
} from "lucide-react";
import { useCustomRoles } from "../../../hooks/permissionHooks/useCustomRoles";
import { useSystemPermissions } from "../../../hooks/permissionHooks/useSystemPermissions";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";

// Permission dependencies - when a permission is selected, its dependencies are automatically selected
const PERMISSION_DEPENDENCIES = {
  // Redeem_Edit and Redeem_Delete require Redeem_Offers access
  "Redeem_Edit": ["Redeem_Offers"],
  "Redeem_Delete": ["Redeem_Offers"],
  
  // Partner management dependencies
  "Edit_Partner": ["View_Partners"],
  "Delete_Partner": ["View_Partners"],
  
  // Deals management dependencies
  "Edit_Offer": ["View_Deals"],
  "Delete_Offer": ["View_Deals"],
  "Blast_Deal": ["View_Deals"],
  
  // Role management dependencies
  "Role_Edit": ["Role_View"],
  "Role_Delete": ["Role_View"],
  "Role_Create": ["Role_View"],
  
  // Subscription dependencies
  "Purchase_Subscription": ["View_Subscription"],
  "View_Subscription_History": ["View_Subscription"],
};

const EditRole = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const { customRoles, updateRole, isUpdating, getRoleById } = useCustomRoles();
  const { permissions, isLoading } = useSystemPermissions();
  const { canEditRoles } = usePermissionCheck();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    permissions: {}
  });

  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [impactAnalysis, setImpactAnalysis] = useState(null);
  const [showImpactWarning, setShowImpactWarning] = useState(false);

  const role = getRoleById(roleId);

  // Initialize form data when role is loaded
  useEffect(() => {
    if (role) {
      const initialData = {
        name: role.name,
        permissions: { ...role.permissions }
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [role]);

  // Analyze impact when permissions change
  useEffect(() => {
    if (originalData && role && roleId) {
      const hasChanges = JSON.stringify(formData.permissions) !== JSON.stringify(originalData.permissions);
      
      if (hasChanges) {
        const impact = {
          hasChanges: true,
          affectedMembers: [],
          changes: []
        };
        setImpactAnalysis(impact);
        setShowImpactWarning(impact.hasChanges && impact.affectedMembers.length > 0);
      } else {
        setImpactAnalysis(null);
        setShowImpactWarning(false);
      }
    }
  }, [formData.permissions, originalData, role, roleId]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Helper function to get all dependencies for a permission
  const getDependencies = (action) => {
    const directDeps = PERMISSION_DEPENDENCIES[action] || [];
    const allDeps = [...directDeps];
    
    directDeps.forEach(dep => {
      const depDeps = getDependencies(dep);
      depDeps.forEach(depDep => {
        if (!allDeps.includes(depDep)) {
          allDeps.push(depDep);
        }
      });
    });
    
    return allDeps;
  };

  // Helper function to find which module a permission belongs to
  const findPermissionModule = (action) => {
    for (const [module, actions] of Object.entries(permissions)) {
      if (actions.includes(action)) {
        return module;
      }
    }
    return null;
  };

  // Handle permission changes with dependency logic
  const handlePermissionChange = (module, action, checked) => {
    setFormData(prev => {
      const newPermissions = { ...prev.permissions };
      
      if (checked) {
        const dependencies = getDependencies(action);
        
        // Add the selected permission
        newPermissions[module] = [...(newPermissions[module] || []), action];
        
        // Add dependencies to their respective modules
        dependencies.forEach(dep => {
          const depModule = findPermissionModule(dep);
          if (depModule && depModule !== module) {
            newPermissions[depModule] = [...(newPermissions[depModule] || []), dep];
          } else if (depModule === module) {
            // Dependency is in the same module, add it if not already present
            if (!newPermissions[module].includes(dep)) {
              newPermissions[module] = [...newPermissions[module], dep];
            }
          }
        });
      } else {
        // When unchecking a permission, also uncheck permissions that depend on it
        const permissionsToRemove = [action];
        
        // Find all permissions that depend on this one
        Object.entries(PERMISSION_DEPENDENCIES).forEach(([dependentAction, deps]) => {
          if (deps.includes(action)) {
            permissionsToRemove.push(dependentAction);
          }
        });
        
        // Remove the permission and its dependents from all modules
        Object.keys(newPermissions).forEach(moduleKey => {
          newPermissions[moduleKey] = (newPermissions[moduleKey] || []).filter(
            perm => !permissionsToRemove.includes(perm)
          );
        });
      }
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  // Handle module toggle (select/deselect all)
  const handleModuleToggle = (module, checked) => {
    const modulePermissions = permissions[module] || [];
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: checked ? [...modulePermissions] : []
      }
    }));
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Role name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Role name must be at least 2 characters";
    }

    const hasPermissions = Object.values(formData.permissions).some(perms => perms.length > 0);
    if (!hasPermissions) {
      newErrors.permissions = "At least one permission must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    try {
      await updateRole({ roleSlug: roleId, roleData: formData });
      navigate('/roles/manage');
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  // Handle field blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Check if user has permission to edit roles
  if (!canEditRoles()) {
    return (
      <div className="bg-white">
        <Navbar page={"Edit Role"} />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 w-16 h-16 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold text-gray-800">Access Restricted</h2>
            <p className="text-gray-500">You don't have permission to edit roles.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !role) {
    return <Loader />;
  }

  return (
    <div className="bg-white">
      <Navbar page={"Edit Role"} />
      
      <div className="p-6 min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex overflow-x-auto flex-wrap gap-1 items-center mb-6 text-xs text-gray-500 sm:gap-2 sm:text-sm"
        >
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors whitespace-nowrap"
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <ChevronRight size={14} className="hidden sm:block" />
          <ChevronRight size={12} className="sm:hidden" />
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors whitespace-nowrap truncate max-w-[100px] sm:max-w-none"
            onClick={() => navigate('/roles-and-permissions')}
          >
            Roles & Permissions
          </span>
          <ChevronRight size={14} className="hidden sm:block" />
          <ChevronRight size={12} className="sm:hidden" />
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors whitespace-nowrap truncate max-w-[80px] sm:max-w-none"
            onClick={() => navigate('/roles/manage')}
          >
            Manage Roles
          </span>
          <ChevronRight size={14} className="hidden sm:block" />
          <ChevronRight size={12} className="sm:hidden" />
          <span className="text-[#FF6C6C] font-medium whitespace-nowrap">Edit Role</span>
        </motion.div>

        {/* Header */}
        <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:justify-between sm:items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex gap-2 items-center sm:gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/roles/manage')}
              className="flex-shrink-0 p-2 text-gray-400 rounded-xl transition-all duration-200 hover:text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>
            <div className="flex gap-2 items-center min-w-0 sm:gap-3">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-[#FF6C6C]/10 via-pink-50 to-white flex-shrink-0">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6C6C]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-800 truncate sm:text-2xl">
                  Edit Role: <span className="text-[#FF6C6C]">{role.name}</span>
                </h1>
                <p className="hidden text-xs text-gray-500 sm:text-sm sm:block">Modify role permissions and details</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Impact Warning */}
        {showImpactWarning && impactAnalysis && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 mb-6 bg-amber-50 rounded-xl border border-amber-200"
          >
            <div className="flex gap-3 items-start">
              <AlertTriangle className="mt-0.5 w-5 h-5 text-amber-600" />
              <div>
                <h3 className="mb-1 font-medium text-amber-800">
                  Impact Warning: {impactAnalysis.affectedMembers.length} team member(s) will be affected
                </h3>
                <p className="mb-3 text-sm text-amber-700">
                  The following changes will affect team members with this role:
                </p>
                <div className="space-y-2">
                  {impactAnalysis.changes.map((change, index) => (
                    <div key={index} className="flex gap-2 items-center text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        change.type === 'added' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {change.type === 'added' ? 'Added' : 'Removed'}
                      </span>
                      <span className="text-amber-700">
                        {change.module}: {change.permissions.join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-center mt-3">
                  <Users className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    Affected members: {impactAnalysis.affectedMembers.map(m => m.name).join(', ')}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="mx-auto max-w-7xl"
        >
          <div className="space-y-8">
            {/* Basic Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <h2 className="mb-6 text-lg font-semibold text-gray-800">Basic Information</h2>
              
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                    placeholder="e.g., Sales Manager, Marketing Lead"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                      errors.name && touched.name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                    }`}
                  />
                  {errors.name && touched.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-800">Permissions</h2>
                <span className="text-sm text-gray-500">
                  Modify the permissions for this role
                </span>
              </div>

              {/* Dependency Information */}
              <div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex gap-3 items-start">
                  <div className="flex flex-shrink-0 justify-center items-center mt-0.5 w-4 h-4 bg-blue-500 rounded-full">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                  <div>
                    <h3 className="mb-1 text-sm font-medium text-blue-800">Permission Dependencies</h3>
                    <p className="text-sm text-blue-700">
                      Some permissions automatically include others. For example, selecting "Edit" or "Delete" 
                      permissions will automatically grant "View" access since you need to see the content first.
                    </p>
                  </div>
                </div>
              </div>

              {errors.permissions && (
                <div className="p-3 mb-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">{errors.permissions}</p>
                </div>
              )}

              <div className="space-y-6">
                {Object.entries(permissions).map(([module, actions]) => {
                  const modulePermissions = formData.permissions[module] || [];
                  const allSelected = actions.length > 0 && actions.every(action => modulePermissions.includes(action));
                  const someSelected = modulePermissions.length > 0 && !allSelected;

                  return (
                    <div key={module} className="p-4 rounded-xl border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex gap-3 items-center">
                          <button
                            type="button"
                            onClick={() => handleModuleToggle(module, !allSelected)}
                            className={`flex items-center justify-center w-5 h-5 rounded border-2 transition-all duration-200 ${
                              allSelected 
                                ? 'bg-[#FF6C6C] border-[#FF6C6C] text-white' 
                                : someSelected
                                ? 'bg-gray-200 border-gray-300'
                                : 'border-gray-300 hover:border-[#FF6C6C]'
                            }`}
                          >
                            {allSelected && <Check className="w-3 h-3" />}
                            {someSelected && <div className="w-2 h-2 bg-gray-600 rounded" />}
                          </button>
                          <h3 className="font-medium text-gray-800">{module}</h3>
                        </div>
                        <span className="text-sm text-gray-500">
                          {modulePermissions.length} of {actions.length} selected
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {actions.map((action) => {
                          const isSelected = modulePermissions.includes(action);
                          // Check if this permission was auto-selected due to other permissions depending on it
                          const isAutoSelected = isSelected && Object.entries(PERMISSION_DEPENDENCIES).some(([dependentAction, deps]) => {
                            if (deps.includes(action)) {
                              const dependentModule = findPermissionModule(dependentAction);
                              return formData.permissions[dependentModule]?.includes(dependentAction);
                            }
                            return false;
                          });
                          
                          return (
                            <label
                              key={action}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                                isSelected 
                                  ? isAutoSelected
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-[#FF6C6C] bg-[#FF6C6C]/5'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handlePermissionChange(module, action, e.target.checked)}
                                className="sr-only"
                              />
                              <div className={`flex items-center justify-center w-4 h-4 rounded border-2 transition-all duration-200 ${
                                isSelected 
                                  ? isAutoSelected
                                    ? 'bg-blue-500 border-blue-500 text-white'
                                    : 'bg-[#FF6C6C] border-[#FF6C6C] text-white' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected && <Check className="w-2.5 h-2.5" />}
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className={`text-sm font-medium ${
                                  isSelected 
                                    ? isAutoSelected
                                      ? 'text-blue-600'
                                      : 'text-[#FF6C6C]' 
                                    : 'text-gray-700'
                                }`}>
                                  {action}
                                </span>
                                {isAutoSelected && (
                                  <span className="text-xs text-blue-500">
                                    Auto-selected (dependency)
                                  </span>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 justify-end items-center p-6 mt-8 bg-white rounded-xl border border-gray-100 shadow-sm">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/roles/manage')}
              className="px-6 py-3 font-medium text-gray-600 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
            >
              Cancel
            </motion.button>
            
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isUpdating}
              className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Update Role
                </>
              )}
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default EditRole; 